import { defineMessages } from 'react-intl';

import type { AdyenError } from '@/lib/types';
import { INTL_PREFIX } from '@/messages/const';

import { commonErrorCodeMessages, type FormattedError } from './common';

const intlPrefix = INTL_PREFIX.PAYMENT;

const messages = defineMessages({
  refused: {
    id: `${intlPrefix}.refused`,
    defaultMessage: 'Payment refused.',
  },
  cancelled: {
    id: `${intlPrefix}.cancelled`,
    defaultMessage: 'Payment cancelled.',
  },
  refusedCommon: {
    id: `${intlPrefix}.refused.common`,
    defaultMessage:
      "<bold>We're sorry, your payment could not be processed. </bold>{br}If you have any questions or require assistance, please call us at <phone>{phoneNumber}</phone>. Our Customer Relations Squad is happy to help.",
  },
  refusedWrongCvc: {
    id: `${intlPrefix}.refused.wrongCvc`,
    defaultMessage:
      "<bold>We're sorry, your payment was unsuccessful.</bold>{br}The security code entered was incorrect.{br}Please try again.{br}If you have any questions or require assistance, our Customer Relations Sqaud is happy to help. Please call us at <phone>{phoneNumber}</phone>.",
  },
  refusedCardLimitFinancing: {
    id: `${intlPrefix}.refused.cardLimit.financing`,
    defaultMessage:
      "<bold>We're sorry, your payment was unsuccessful.</bold>{br}It looks like your card has reached its limit.<ul>To complete your purchase, you may consider the following options:<li>Split the payment across multiple payment methods</li><li>Apply for financing at checkout</li></ul>If you have any questions or require assistance, our Customer Relations Squad is happy to help. Please call us at <phone>{phoneNumber}</phone>.",
  },
  refusedCardLimit: {
    id: `${intlPrefix}.refused.cardLimit`,
    defaultMessage:
      "<bold>We're sorry, your payment was unsuccessful.</bold>{br}It looks like your card has reached its limit.{br}To complete your purchase, you can split the payment across multiple payment methods.{br}If you have any questions or require assistance, our Customer Relations Squad is happy to help. Please call us at <phone>{phoneNumber}</phone>.",
  },
});

export const getAdyenError = (
  error: FormattedError<AdyenError>,
  isFinancingAvailable?: boolean,
  phoneNumber?: string,
) => {
  const message = getAdyenErrorCodeMessage(error, isFinancingAvailable);

  const refusedMessageValues = {
    bold: (chunks: string) => <strong>{chunks}</strong>,
    br: <br />,
    ul: (chunks: React.ReactNode) => (
      <ul style={{ listStyleType: 'circle' }}>{chunks}</ul>
    ),
    li: (chunks: string) => (
      <li style={{ marginLeft: 25, lineHeight: 1.5 }}>{chunks}</li>
    ),
    phoneNumber,
    phone: (chunks: string) => (
      <a href={`tel:${chunks}`} style={{ textDecoration: 'underline' }}>
        {chunks}
      </a>
    ),
  };
  const values = error.code === 'Refused' ? refusedMessageValues : error.values;

  return {
    message,
    values,
  };
};

// https://docs.adyen.com/development-resources/refusal-reasons/
enum RefusalReasonCode {
  None = '0',
  Refused = '2',
  Referral = '3',
  AcquirerError = '4',
  BlockedCard = '5',
  ExpiredCard = '6',
  InvalidAmount = '7',
  InvalidCardNumber = '8',
  IssuerUnavailable = '9',
  NotSupported = '10',
  ThreeDNotAuthenticated = '11',
  NotEnoughBalance = '12',
  AcquirerFraud = '14',
  Cancelled = '15',
  ShopperCancelled = '16',
  InvalidPin = '17',
  PinTriesExceeded = '18',
  PinValidationNotPossible = '19',
  FRAUD = '20',
  NotSubmitted = '21',
  FRAUDCANCELLED = '22',
  TransactionNotPermitted = '23',
  CVCDeclined = '24',
  RevocationOfAuth = '26',
  DeclinedNonGeneric = '27',
  WithdrawalAmountExceeded = '28',
  WithdrawalCountExceeded = '29',
  IssuerSuspectedFraud = '31',
  AVSDeclined = '32',
  CardRequiresOnlinePin = '33',
  NoCheckingAccountAvailableOnCard = '34',
  NoSavingsAccountAvailableOnCard = '35',
  MobilePinRequired = '36',
  ContactlessFallback = '37',
  AuthenticationRequired = '38',
  RReqNotReceivedFromDS = '39',
  CurrentAIDIsInPenaltyBox = '40',
  CVMRequiredRestartPayment = '41',
  ThreeDSAuthenticationError = '42',
  TransactionBlockedByAdyenToPreventExcessiveRetryFees = '46',
}

export const getAdyenErrorCodeMessage = (
  error: FormattedError<AdyenError>,
  isFinancingAvailable?: boolean,
) => {
  switch (error.code) {
    case 'Refused':
      switch (error.message) {
        case RefusalReasonCode.NotEnoughBalance:
        case RefusalReasonCode.WithdrawalAmountExceeded:
        case RefusalReasonCode.WithdrawalCountExceeded: {
          return isFinancingAvailable
            ? messages.refusedCardLimitFinancing
            : messages.refusedCardLimit;
        }
      }
      return messages.refusedCommon;

    case 'Cancelled':
      return messages.cancelled;

    default:
      return error.message
        ? Object.values(RefusalReasonCode).includes(
            error.message as RefusalReasonCode,
          )
          ? messages.refusedCommon
          : error.message
        : commonErrorCodeMessages.genericPaymentErrorWithoutMessage;
  }
};
